import './Animation.scss';
import React, { FC, Suspense, lazy, useEffect, useMemo } from 'react';
import type { Animation as AnimationFields } from '@/generated/graphql';
import { useUserCurrent } from '@/components/provider/UserCurrentProvider';
import classNames from 'classnames';
import type { ImageSize as FrameSize } from '@/utils';
import { IMAGE_QUALITY_PROFILES } from '@/utils';
import { calculateSmartUserQualityOptions } from './AnimationConfigPipeline';
import { useAnimationEngine } from './useAnimationEngine';
import { buildSingleFrameUrl, getFramesArray } from './AnimationUtilities';
import { AnimationModeConfig } from './AnimationTypes';

// Presentational & Structural Core Layout Chunks
import AnimationPlayer from './subcomponents/AnimationPlayer';
import AnimationProgress from './subcomponents/AnimationProgress';
import AnimationControlPrimary from './subcomponents/AnimationControlPrimary';
import AnimationControlSecondary from './subcomponents/AnimationControlSecondary';
import AnimationControlEdit from './subcomponents/AnimationControlEdit';
import Icon from '@/components/extra/Icon/Icon';

// Main Heavy Editing Shell Bundles
const AnimationEdit = lazy(
    () => import(/* webpackChunkName: "edit-main" */ './AnimationEdit'),
);
const EditShell = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/edit/EditShell/EditShell'
        ),
);

interface AnimationProps {
    fields: AnimationFields;
    panelOwnerId?: number;
    defaultFrameSize?: FrameSize;
    className?: string;
}

const MOCK_TIMELAPSE_MODE: AnimationModeConfig = {
    modeType: 'TIMELAPSE',
    defaultSize: 'm',
    defaultFps: 12,
    minFps: 1,
    maxFps: 60,
    selectableSpeeds: [1, 3, 6, 12, 30, 60],
    allowCrossDissolve: true,
    fallbackStrategy: 'wait',
};

const MOCK_SLIDESHOW_MODE: AnimationModeConfig = {
    modeType: 'SLIDESHOW',
    defaultSize: 'l',
    defaultFps: 1,
    minFps: 0.2,
    maxFps: 4,
    selectableSpeeds: [0.2, 0.5, 1, 2, 4],
    allowCrossDissolve: false,
};

const Animation: FC<AnimationProps> = ({
    fields,
    panelOwnerId,
    defaultFrameSize = 'm',
    className,
}) => {
    const { userCurrent, refreshBattery } = useUserCurrent();
    const uniqueId = fields?.id;
    const activePlaybackMode = MOCK_TIMELAPSE_MODE;
    const frames = getFramesArray(fields?.alignmentData);
    const alignmentLabels: string[] = fields?.alignmentLabels || [];
    const defaultAlignmentIndex: number = fields?.alignmentROIsIndex ?? 0;

    useEffect(() => {
        refreshBattery();
    }, [refreshBattery]);

    useAnimationEngine({
        uniqueId,
        frames,
        subDir: fields?.subDir,
        activePlaybackMode,
        deviceProfile: userCurrent?.deviceProfile,
        w: fields?.w,
        h: fields?.h,
        alignmentIndex: defaultAlignmentIndex,
    });

    // TODO: refine so only contentEditors with perms for this.
    const hasEditPerms = userCurrent?.isContentEditor;
    const isEditModeWithPerms = !!(
        userCurrent?.isEditMode === 'MEDIA' && panelOwnerId
    );

    const qualitySelections = useMemo(() => {
        if (!fields || frames.length === 0) return [];

        if (hasEditPerms) {
            const engineSetup = calculateSmartUserQualityOptions(
                activePlaybackMode,
                userCurrent?.deviceProfile,
            );
            const activeAutoId =
                engineSetup.find((opt) => opt.value === 'auto')?.id || 'm';

            return [
                {
                    value: 'auto',
                    id: activeAutoId,
                    label: `Auto (${activeAutoId.toUpperCase()})`,
                },
                ...IMAGE_QUALITY_PROFILES.map((profile) => ({
                    value: profile.id,
                    id: profile.id,
                    label: profile.id.toUpperCase(),
                })),
            ];
        } else {
            // Regular Visitor Pass: Generate exactly 3 smart options
            return calculateSmartUserQualityOptions(
                activePlaybackMode,
                userCurrent?.deviceProfile,
            );
        }
    }, [
        hasEditPerms,
        userCurrent?.deviceProfile,
        fields,
        frames.length,
        activePlaybackMode,
    ]);

    if (!fields || frames.length === 0) return null;

    let stillIndex = frames.length - 1;
    if (fields?.stillFrameStem) {
        const foundIndex = frames.findIndex(
            (frame) => frame.frameStem === fields.stillFrameStem,
        );
        if (foundIndex !== -1) stillIndex = foundIndex;
    }

    const initialImageUrl =
        frames.length > 0 && stillIndex >= 0
            ? buildSingleFrameUrl(
                  fields.subDir,
                  frames[stillIndex].frameStem,
                  defaultFrameSize,
              )
            : '';

    const animationContent = (
        <section className={classNames('Animation', className)}>
            <h2 className="Animation_title">{fields.title}</h2>
            <div
                className="Animation_container"
                id={`AnimationContainer_${uniqueId}`}
            >
                <AnimationPlayer
                    uniqueId={uniqueId}
                    initialImageUrl={initialImageUrl}
                    fields={fields}
                />

                <AnimationProgress uniqueId={uniqueId} />

                <div
                    id={`AnimationControls_${uniqueId}`}
                    className="Animation_controls"
                >
                    <AnimationControlPrimary uniqueId={uniqueId} />

                    {hasEditPerms && (
                        <AnimationControlEdit
                            uniqueId={uniqueId}
                            fields={fields}
                            frameCount={frames.length}
                        />
                    )}

                    <AnimationControlSecondary
                        uniqueId={uniqueId}
                        activePlaybackMode={activePlaybackMode}
                        qualitySelections={qualitySelections}
                        alignmentLabels={alignmentLabels}
                        defaultAlignmentIndex={defaultAlignmentIndex}
                    />
                </div>

                {/* Large Mobile-only Layer Button Centered Directly Over Canvas Workspace */}
                <button
                    id={`MobilePlayOverlayBtn_${uniqueId}`}
                    className="Animation_mobilePlayOverlayBtn"
                >
                    <Icon icon="caret" />
                </button>
            </div>
        </section>
    );

    if (isEditModeWithPerms) {
        const passEditState = {
            editKey: fields.id,
            componentType: 'ANIMATION',
            fields: {},
            localFields: {
                title: fields.title,
                caption: fields.caption,
                copyright: fields.copyright?.id,
                copyrightUrl: fields.copyrightUrl,
                animationType: fields.animationType,
                stillFrameStem: fields.stillFrameStem,
            },
            ownerId: panelOwnerId,
        };

        return (
            <Suspense fallback={animationContent}>
                <EditShell passEditState={passEditState}>
                    <AnimationEdit
                        animationId={fields.id}
                        existingAnimationFields={fields}
                    >
                        {animationContent}
                    </AnimationEdit>
                </EditShell>
            </Suspense>
        );
    }

    return animationContent;
};

export default Animation;
